import { NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialogResultData,
  DialogService,
  DIALOG_RETURN_TYPES,
} from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, skipWhile, switchMap, take } from 'rxjs/operators';

import { Cycle, ObservationSettingEnum, ObservationSettings } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getSelectedCycle, getNextCycleNumber } from '../../store/selectors/cycle.selectors';
import { cycleNumberValidator } from '../../validators/cycle-number-validator';
import {
  ObservationSettingsComponent,
  ObservationSettingsDialogResultData,
} from '../observation-settings/observation-settings.component';

@Component({
  selector: 'app-general',
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    AgrNumberDirective,
    NgIf,
    MatError,
    RouterLink,
    MatButton,
    TranslatePipe,
  ],
})
export class GeneralComponent implements OnInit {
  cycle$: Observable<Cycle> = this.store.select(getSelectedCycle);
  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getPath));

  observationSettings: ObservationSettings = {
    animalWeightInGrams: ObservationSettingEnum.Daily,
    uniformity: ObservationSettingEnum.Daily,
    coefficientOfVariation: ObservationSettingEnum.Daily,
    feedName: ObservationSettingEnum.Daily,
    feedIntakeInGramsPerAnimal: ObservationSettingEnum.Daily,
    mortality: ObservationSettingEnum.Daily,
    waterIntakeInLitersTotal: ObservationSettingEnum.Daily,
    numberOfMinutesLight: ObservationSettingEnum.Daily,
    lightIntensityInLux: ObservationSettingEnum.Daily,
    notes: ObservationSettingEnum.Daily,
  };

  form: FormGroup = new FormGroup({
    cycleNumber: new FormControl(null, Validators.required, cycleNumberValidator(this.store)),
    description: new FormControl(''),
  });

  constructor(
    private readonly store: Store<RootState>,
    private readonly dialogService: DialogService,
    private readonly localeService: LocaleService,
  ) {}

  ngOnInit() {
    this.routeUrl$.subscribe((url) => {
      if (url.endsWith('/cycle/add')) {
        this.store.select(getNextCycleNumber).subscribe((nextCycleNumber) => {
          this.form.controls.cycleNumber.setValue(nextCycleNumber);
        });
      } else {
        this.cycle$
          .pipe(
            skipWhile((cycle) => cycle === null || cycle === undefined),
            take(1),
          )
          .subscribe((cycle) => {
            this.form.controls.cycleNumber.setValue(cycle.cycleNumber);
            this.form.controls.description.setValue(cycle.description);
            if (cycle.observationSettings) {
              this.observationSettings = cycle.observationSettings;
            }
          });
      }
    });
  }

  private getCycle(cycle: Cycle) {
    return {
      cycle: {
        ...cycle,
        ...this.form.value,
        id: cycle ? cycle.id : undefined,
        observationSettings: this.observationSettings,
      } as Cycle,
    };
  }

  openObservationsettings() {
    this.cycle$
      .pipe(
        take(1),
        switchMap((cycle) =>
          this.dialogService
            .form(ObservationSettingsComponent, {
              settings: this.observationSettings,
              setPendingChanges: false,
              pushHistoryState: false,
              confirmButtonText: this.localeService.translate('confirm'),
            })
            .pipe(map((dialogResult) => [dialogResult, cycle])),
        ),
      )
      .subscribe(
        ([dialogResult, _cycle]: [
          AbstractDialogResultData<ObservationSettingsDialogResultData>,
          Cycle,
        ]) => {
          if (dialogResult.status === DIALOG_RETURN_TYPES.confirm) {
            this.observationSettings = dialogResult.result.settings;
          }
        },
      );
  }

  save() {
    if (this.form.valid) {
      this.cycle$.pipe(take(1)).subscribe((cycle) => {
        if (cycle) {
          this.store.dispatch(cycleActions.upsertCycleGeneral(this.getCycle(cycle)));
        } else {
          this.store.dispatch(cycleActions.createCycle(this.getCycle(cycle)));
        }
      });
    }
  }
}
